import { Toast } from '@zep-us/zus/toast';
import { type TFunction } from 'i18next';
import { zusToast, type ZusToastId } from '@zep/toast';

/** 진행 토스트를 띄우기 전에 기다리는 시간. 대부분의 저장은 이보다 빨리 끝나므로 오래 걸릴 때만 알린다. */
const LOADING_DELAY_MS = 600;

/** 결과 토스트가 머무는 시간. 액션(`보러 가기`)을 누를 여유를 준다. */
const RESULT_DURATION_MS = 4_000;

/** 진행·결과 토스트 위치. 진행 → 결과가 같은 id 로 교체되므로 모두 같은 값을 준다. */
export const QUIZ_FOLDER_TOAST_POSITION = 'top-center' as const;
export interface QuizSaveSuccessFeedback {
  title: string;
  /** 있을 때만 `보러 가기` 가 붙는다. 갈 곳이 없으면 버튼도 없다. */
  href?: string;
  actionLabel?: string;
  /** 목적지로 보낸다. 라우터를 이 모듈이 알지 않도록 호출부가 넘긴다. */
  onNavigate?: (href: string) => void;
}
export interface QuizSaveFailureFeedback {
  title: string;
  /**
   * 있을 때만 `다시 시도` 가 붙는다.
   *
   * ⚠️ 멱등이 보장된 요청에만 넘긴다 — 복사는 다시 부르면 복사본이 둘 생길 수 있다.
   */
  onRetry?: () => void;
}
export interface QuizSaveFeedbackSession {
  success: (feedback: QuizSaveSuccessFeedback) => void;
  failure: (feedback: QuizSaveFailureFeedback) => void;
  /** 결과 대신 알럿·확인 다이얼로그로 흐름이 넘어갈 때. 진행 토스트만 걷는다. */
  dismiss: () => void;
}

/** 진행 상태가 없는 이동 경로에서도 동일한 ZUS 결과 토스트를 쓴다. */
export const showQuizSaveSuccess = ({
  title,
  href,
  actionLabel,
  onNavigate
}: QuizSaveSuccessFeedback & {
  actionLabel: string;
}, replaceId?: ZusToastId) => {
  const canNavigate = !!href && !!onNavigate;
  const resultId = zusToast.success(title, {
    id: replaceId,
    icon: null,
    duration: RESULT_DURATION_MS,
    position: QUIZ_FOLDER_TOAST_POSITION,
    action: canNavigate ? {
      label: actionLabel,
      onClick: () => {
        zusToast.dismiss(resultId);
        onNavigate(href);
      }
    } : undefined
  });
};

/**
 * 폴더 저장·이동의 진행/완료/실패 안내를 한 묶음으로 다룬다.
 *
 * 표현만 갖는다. 어떤 요청을 보냈는지, 재시도 가능한지, 어디로 보낼지는 호출부가 정해서 넘긴다.
 *
 * 진행 토스트가 이미 떴다면 같은 id 로 교체한다.
 * 실패는 `zusToast.error`(destructive) 대신 `custom` 안에서 ZUS `Toast` 를 `default` 로 그린다.
 */
export const beginQuizSaveFeedback = (t: TFunction<['common']>): QuizSaveFeedbackSession => {
  let loadingToastId: ZusToastId | undefined;
  let settled = false;
  const timer = setTimeout(() => {
    if (settled) return;
    loadingToastId = zusToast.loading(t('common:folderSave.loading'), {
      position: QUIZ_FOLDER_TOAST_POSITION
    });
  }, LOADING_DELAY_MS);

  /** 진행 토스트 예약을 끊는다. 결과가 정해졌거나 흐름이 다른 데로 넘어갈 때 부른다. */
  const settle = () => {
    settled = true;
    clearTimeout(timer);
  };
  return {
    success: feedback => {
      settle();
      showQuizSaveSuccess({
        ...feedback,
        actionLabel: feedback.actionLabel ?? t('common:folderSave.viewFolder')
      }, loadingToastId);
    },
    failure: ({
      title,
      onRetry
    }) => {
      settle();
      zusToast.custom(({
        dismiss
      }) => <Toast variant={'default'} title={title} onClose={dismiss} action={onRetry ? {
        label: t('common:folderSave.retry'),
        onClick: () => {
          dismiss();
          onRetry();
        }
      } : undefined} />, {
        id: loadingToastId,
        duration: RESULT_DURATION_MS,
        position: QUIZ_FOLDER_TOAST_POSITION
      });
    },
    dismiss: () => {
      settle();
      if (loadingToastId !== undefined) zusToast.dismiss(loadingToastId);
    }
  };
};